import Client from "fhir-kit-client";
import type { Appointment, Bundle, ServiceRequest } from "fhir/r5";
import { FollowUpItem, FollowUpStatus } from "../models/FollowUp";
import { VALUE_CODES, ValueCode } from "../models/OverviewDashboard";
import { getUpCareThemeCode, getUpCareThemeDisplay } from "../utils/UpCareThemes";
import LocationService from "./LocationService";

const VALUE_SYSTEM = "https://chunancy.fr/fhir/CodeSystem/upcare-values";
const REQUEST_TYPE_CODES = {
    needs: ["besoin", "demande", "demande-atelier-formation", "demande-formation"],
    proposals: ["proposition", "proposition-atelier", "bonne-pratique"]
};

interface FollowUpServiceConfig {
    fhirBaseUrl: string;
}

interface FollowUpSearchContext {
    organizationReferences: string[];
    organizationNames: Record<string, string>;
}

class FollowUpService {
    private client: Client;
    private locationService: LocationService;

    constructor(config: FollowUpServiceConfig) {
        this.client = new Client({ baseUrl: config.fhirBaseUrl });
        this.locationService = new LocationService(config);
    }

    async search(context: FollowUpSearchContext): Promise<FollowUpItem[]> {
        const [serviceRequests, appointments, locations] = await Promise.all([
            this.searchServiceRequests(),
            this.searchAppointments(),
            this.locationService.searchByManagingOrganizations(context.organizationReferences)
        ]);

        const organizationReferences = new Set(context.organizationReferences);
        const locationReferences = new Set(locations.map((location) => `Location/${location.id}`));
        const locationOrganization = new Map(
            locations.map((location) => [
                `Location/${location.id}`,
                location.organizationReference
            ])
        );

        const requests = serviceRequests
            .filter((request) => this.hasUpCareValue(request))
            .filter((request) => this.hasRelevantReference(request, organizationReferences))
            .map((request) => this.mapServiceRequest(request, context.organizationNames));

        const events = appointments
            .filter((appointment) => this.hasUpCareValue(appointment))
            .filter((appointment) => this.isRelevantAppointment(
                appointment,
                organizationReferences,
                locationReferences
            ))
            .map((appointment) => this.mapAppointment(
                appointment,
                context.organizationNames,
                locationOrganization
            ));

        return [...requests, ...events]
            .filter((item): item is FollowUpItem => !!item)
            .sort((a, b) => this.sortDate(b) - this.sortDate(a));
    }

    private async searchServiceRequests(): Promise<ServiceRequest[]> {
        const bundle = await this.client.search({
            resourceType: "ServiceRequest",
            searchParams: {
                _count: 100,
                _sort: "-authored"
            }
        }) as Bundle<ServiceRequest>;

        return bundle.entry
            ?.map((entry) => entry.resource)
            .filter((resource): resource is ServiceRequest =>
                !!resource && resource.resourceType === "ServiceRequest"
            )
            .filter((request) => !["revoked", "entered-in-error"].includes(request.status ?? "")) ?? [];
    }

    private async searchAppointments(): Promise<Appointment[]> {
        const bundle = await this.client.search({
            resourceType: "Appointment",
            searchParams: {
                _count: 100,
                _sort: "-date"
            }
        }) as Bundle<Appointment>;

        return bundle.entry
            ?.map((entry) => entry.resource)
            .filter((resource): resource is Appointment =>
                !!resource && resource.resourceType === "Appointment"
            )
            .filter((appointment) => !["cancelled", "entered-in-error"].includes(appointment.status ?? "")) ?? [];
    }

    private mapServiceRequest(
        request: ServiceRequest,
        organizationNames: Record<string, string>
    ): FollowUpItem | undefined {
        const status = this.getRequestStatus(request);
        if (!status) return undefined;

        const requestAny = request as any;
        const organization = this.findOrganizationReference(request);
        const quantity = requestAny.quantityQuantity?.value;
        const themeCode = getUpCareThemeCode(request);

        return {
            id: `ServiceRequest/${request.id ?? crypto.randomUUID()}`,
            status,
            statusLabel: status === "proposal" ? "Proposition" : "Demande",
            values: this.getValueCodes(request),
            nature: this.getRequestNature(requestAny, status),
            themeCode,
            theme: getUpCareThemeDisplay(themeCode),
            title: this.firstText(requestAny.code) ??
                this.firstText(requestAny.orderDetail?.[0]) ??
                requestAny.patientInstruction ??
                "Action d'accompagnement",
            carriedBy: this.getOrganizationDisplay(organization, organizationNames),
            engagement: typeof quantity === "number" && Number.isFinite(quantity) ? quantity : undefined,
            date: requestAny.occurrenceDateTime ?? requestAny.occurrencePeriod?.start,
            publishedAt: request.authoredOn ?? request.meta?.lastUpdated,
            raw: request
        };
    }

    private mapAppointment(
        appointment: Appointment,
        organizationNames: Record<string, string>,
        locationOrganization: Map<string, string | undefined>
    ): FollowUpItem {
        const appointmentAny = appointment as any;
        const organization = this.findAppointmentOrganizationReference(
            appointment,
            locationOrganization
        );
        const status: FollowUpStatus = appointment.status === "fulfilled"
            ? "completed"
            : "scheduled";
        const themeCode = getUpCareThemeCode(appointment);

        const attendees = appointment.participant
            ?.filter((participant) => participant.type?.some((type) =>
                type.coding?.some((coding) => coding.code === "ATND")
            ))
            .filter((participant) => participant.status === "accepted" || participant.status === "tentative")
            .length;

        return {
            id: `Appointment/${appointment.id ?? crypto.randomUUID()}`,
            status,
            statusLabel: status === "completed" ? "Réalisé" : "Programmé",
            values: this.getValueCodes(appointment),
            nature: this.firstText(appointmentAny.appointmentType) ?? "Atelier / formation",
            themeCode,
            theme: getUpCareThemeDisplay(themeCode),
            title: appointment.description ?? "Atelier / formation",
            carriedBy: this.getOrganizationDisplay(organization, organizationNames),
            engagement: attendees && attendees > 0 ? attendees : undefined,
            date: appointment.start,
            publishedAt: appointment.meta?.lastUpdated,
            raw: appointment
        };
    }

    private getRequestNature(request: any, status: FollowUpStatus): string {
        const actionType = request.code?.coding?.find((coding: any) =>
            coding?.system === "https://chunancy.fr/fhir/CodeSystem/upcare-action-type"
        )?.display;
        if (typeof actionType === "string" && actionType.trim()) return actionType.trim();
        return status === "proposal" ? "Proposition" : "Besoin";
    }

    private getRequestStatus(request: ServiceRequest): FollowUpStatus | undefined {
        if (this.hasAnyCode(request, REQUEST_TYPE_CODES.needs)) return "request";
        if (this.hasAnyCode(request, REQUEST_TYPE_CODES.proposals)) return "proposal";
        return undefined;
    }

    private hasUpCareValue(resource: unknown): boolean {
        return this.getValueCodes(resource).length > 0;
    }

    private getValueCodes(resource: unknown): ValueCode[] {
        const values = this.collectCodings(resource)
            .filter((coding) => coding.system === VALUE_SYSTEM && !!coding.code)
            .map((coding) => coding.code as ValueCode)
            .filter((code) => VALUE_CODES.includes(code));

        return Array.from(new Set(values));
    }

    private hasAnyCode(resource: unknown, codes: string[]): boolean {
        return this.collectCodings(resource).some((coding) =>
            !!coding.code && codes.includes(coding.code)
        );
    }

    private collectCodings(resource: unknown): Array<{ system?: string; code?: string; display?: string }> {
        const result: Array<{ system?: string; code?: string; display?: string }> = [];
        const visit = (value: unknown) => {
            if (!value || typeof value !== "object") return;
            if (Array.isArray(value)) {
                value.forEach(visit);
                return;
            }

            const object = value as Record<string, unknown>;
            if (typeof object.code === "string") {
                result.push({
                    system: typeof object.system === "string" ? object.system : undefined,
                    code: object.code,
                    display: typeof object.display === "string" ? object.display : undefined
                });
            }
            Object.values(object).forEach(visit);
        };

        visit(resource);
        return result;
    }

    private hasRelevantReference(resource: unknown, references: Set<string>): boolean {
        let found = false;
        const visit = (value: unknown) => {
            if (found || !value || typeof value !== "object") return;
            if (Array.isArray(value)) {
                value.forEach(visit);
                return;
            }

            const object = value as Record<string, unknown>;
            if (typeof object.reference === "string" && references.has(object.reference)) {
                found = true;
                return;
            }
            Object.values(object).forEach(visit);
        };

        visit(resource);
        return found;
    }

    private isRelevantAppointment(
        appointment: Appointment,
        organizationReferences: Set<string>,
        locationReferences: Set<string>
    ): boolean {
        return appointment.participant?.some((participant) => {
            const reference = participant.actor?.reference;
            return !!reference && (
                organizationReferences.has(reference) ||
                locationReferences.has(reference)
            );
        }) ?? false;
    }

    private findOrganizationReference(resource: unknown): { reference?: string; display?: string } | undefined {
        let match: { reference?: string; display?: string } | undefined;
        const visit = (value: unknown) => {
            if (match || !value || typeof value !== "object") return;
            if (Array.isArray(value)) {
                value.forEach(visit);
                return;
            }

            const object = value as Record<string, unknown>;
            if (typeof object.reference === "string" && object.reference.startsWith("Organization/")) {
                match = {
                    reference: object.reference,
                    display: typeof object.display === "string" ? object.display : undefined
                };
                return;
            }
            Object.values(object).forEach(visit);
        };

        visit(resource);
        return match;
    }

    private findAppointmentOrganizationReference(
        appointment: Appointment,
        locationOrganization: Map<string, string | undefined>
    ): { reference?: string; display?: string } | undefined {
        const direct = appointment.participant
            ?.map((participant) => participant.actor)
            .find((actor) => actor?.reference?.startsWith("Organization/"));
        if (direct) return direct;

        const locationReference = appointment.participant
            ?.map((participant) => participant.actor?.reference)
            .find((reference) => !!reference && locationOrganization.has(reference));
        const organizationReference = locationReference
            ? locationOrganization.get(locationReference)
            : undefined;

        return organizationReference ? { reference: organizationReference } : undefined;
    }

    private getOrganizationDisplay(
        organization: { reference?: string; display?: string } | undefined,
        organizationNames: Record<string, string>
    ): string {
        if (organization?.display) return organization.display;
        if (organization?.reference && organizationNames[organization.reference]) {
            return organizationNames[organization.reference];
        }
        return organization?.reference?.replace("Organization/", "") ?? "—";
    }

    private firstText(value: unknown): string | undefined {
        if (!value || typeof value !== "object") return undefined;
        const object = value as Record<string, any>;
        if (typeof object.text === "string" && object.text.trim()) return object.text.trim();
        const display = object.coding?.find((coding: any) => typeof coding?.display === "string")?.display;
        return typeof display === "string" && display.trim() ? display.trim() : undefined;
    }

    private sortDate(item: FollowUpItem): number {
        const candidate = item.date ?? item.publishedAt;
        if (!candidate) return 0;
        const value = new Date(candidate).getTime();
        return Number.isFinite(value) ? value : 0;
    }
}

export default FollowUpService;
